import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, ViewChild } from '@angular/core'
import { SimpleSize } from '@engineering11/ui-lib/e11-ui-config'
import { pick } from '@engineering11/utility'
import * as d3 from 'd3'
import { ITraitDots } from '../core/culture-index.model'
import { CultureIndexChartConnectionsComponent } from './components/chart-connections.component'
import { CultureIndexChartFrameComponent } from './components/chart-frame.component'
import { CultureIndexChartNodesComponent } from './components/chart-nodes.component'
import { CultureIndexChartCLinesComponent } from './components/chart-special-lines.component'
import { CONNECTIONS, NODE_COLOR_MAP, NODES } from './core/culture-index-chart.constant'
import {
  FontConfig,
  IInnerDimensions,
  INode,
  INodeInterConnection,
  ISpecialConnection,
  ISvgScales,
  SVGComponents,
  SvgNode,
  TraitDotsType,
} from './core/culture-index-chart.model'
import { getCoreConfig } from './utils/culture-index-chart.util'
import { calculateDimensions, getFontSize, getGroupClassNames, getOneOfDotsType, getScales } from './utils/d3-utils'

type DrawHandlerCallBackArgs = ReturnType<typeof calculateDimensions> & ReturnType<typeof getScales> & { fontSize: number; svg: SvgNode }
type DrawHandlerDrawArgs = {
  drawConnectionsArgs: ISvgScales
  drawNodesArgs: ISvgScales & Pick<IInnerDimensions, 'innerHeight'>
  drawCLinesArgs: ISvgScales & Pick<IInnerDimensions, 'innerHeight'>
  drawEmptyStateArgs: ISvgScales & FontConfig
}

@Component({
  selector: 'culture-index-chart-core',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CultureIndexChartFrameComponent,
    CultureIndexChartNodesComponent,
    CultureIndexChartConnectionsComponent,
    CultureIndexChartCLinesComponent,
  ],
  standalone: true,
  template: `
    <svg #svgRef class="e11-w-full e11-h-full e11-font-sans" overflow="visible"></svg>
    <culture-index-chart-frame [coreConfig]="coreConfig" [euText]="euText"></culture-index-chart-frame>
    <culture-index-chart-nodes [coreConfig]="coreConfig"></culture-index-chart-nodes>
    <culture-index-chart-connections [coreConfig]="coreConfig"></culture-index-chart-connections>
    <culture-index-chart-c-lines [coreConfig]="coreConfig"></culture-index-chart-c-lines>
  `,
})
export class CultureIndexChartCore implements AfterViewInit {
  @Input() size: Omit<SimpleSize, 'lg'> = 'md'
  _loading?: boolean

  get loading() {
    return this._loading
  }
  @Input() set loading(value: boolean | undefined) {
    this._loading = value
    this.drawUpdate()
  }
  @Input() emptyStateText: string = 'No Ideal Pattern'

  /**
   * Facilitator Patterns
   */
  @Input() set traitDots(value: ITraitDots | undefined) {
    this.handleTraitDotsChange(value)
  }

  /**
   * Candidate Patterns
   */
  @Input() set compareTraitDots(value: ITraitDots | undefined) {
    this.handleCompareTraitDotsChange(value)
  }

  /**
   * EU Text
   */
  @Input() euText: string = 'EU = 41 - 49'

  /**
   * Chart root svg reference
   */
  @ViewChild('svgRef') svgRef!: ElementRef<SVGSVGElement>

  /**
   * Chart frame and axis reference
   */
  @ViewChild(CultureIndexChartFrameComponent) frameRef!: CultureIndexChartFrameComponent

  /**
   * Chart node reference
   */
  @ViewChild(CultureIndexChartNodesComponent) nodesRef!: CultureIndexChartNodesComponent

  /**
   * Chart links reference
   */
  @ViewChild(CultureIndexChartConnectionsComponent) connectionsRef!: CultureIndexChartConnectionsComponent

  /**
   * Chart c line reference
   */
  @ViewChild(CultureIndexChartCLinesComponent) cLinesRef!: CultureIndexChartCLinesComponent

  /**
   * Converts culture index pattern inputs into drawable nodes
   */
  handleTraitDotsChange(value: ITraitDots | undefined) {
    this.handleChangeAndDrawUpdate(() => {
      if (!value) {
        this.#nodes = undefined
        return
      }
      this.#nodes = NODES.map(node => {
        return {
          ...node,
          x: value[node.id],
          color: NODE_COLOR_MAP[node.id],
        }
      })
      const newConnection: ISpecialConnection = {
        position: value.Line,
        color: '#FB0015',
      }
      this.#cLine = newConnection
    })
  }

  /**
   * Converts culture index pattern inputs into drawable nodes
   */
  handleCompareTraitDotsChange(value: ITraitDots | undefined) {
    this.handleChangeAndDrawUpdate(() => {
      if (!value) {
        this.#compareNodes = undefined
        return
      }
      this.#compareNodes = NODES.map(node => {
        return {
          ...node,
          x: value[node.id],
          size: 'small',
        }
      })
      const newConnection: ISpecialConnection = {
        position: value.Line,
        color: '#000',
        dashed: true,
        size: 1,
      }
      this.#compareCLine = newConnection
    })
  }

  /**
   * Helper method to draw updates immediately after changes
   */
  handleChangeAndDrawUpdate(cb: Function) {
    cb()
    if (this.isInEmptyState) {
      setTimeout(() => this.draw())
      return
    }
    this.drawUpdate()
  }

  /**
   * Drawable Pattern Nodes (Facilitator & Candidate Pattern)
   */
  #nodes?: INode[]
  #compareNodes?: INode[]

  /**
   * Drawable Special Connections / Links (Facilitator & Candidate Targets)
   */
  #cLine?: ISpecialConnection
  #compareCLine?: ISpecialConnection

  /**
   * Drawable Connections / Links (Facilitator & Candidate Node Link)
   */
  #connections: INodeInterConnection[] = CONNECTIONS

  /**
   * Core config
   */
  readonly coreConfig = getCoreConfig()

  ngAfterViewInit(): void {
    this.draw()
  }

  @HostListener('window:resize')
  onResize() {
    this.draw()
  }

  /**
   * To track if the chart is in empty state
   */
  private isInEmptyState = false

  /**
   * Writes empty state text
   */
  private drawEmptyState(
    svg: SvgNode,
    {
      yScale,
      xScale,
      fontSize,
    }: ISvgScales & {
      fontSize: number
    }
  ) {
    svg
      .append('text')
      .attr('x', xScale(7))
      .attr('y', yScale(3))
      .attr('text-anchor', 'middle')
      .attr('font-size', `${fontSize}px`)
      .attr('font-weight', 'bold')
      .text(this.emptyStateText)
      .attr('fill', () => (this.loading ? '#e0e0e0' : '#000'))
      .style('opacity', 0)
      .transition()
      .duration(800)
      .style('opacity', 1)

    this.isInEmptyState = true
  }

  /**
   * Clears nodes and links and write empty state text
   */
  private cleanNodesAndLinks(svg: SvgNode, onlyCompare?: boolean) {
    const traitDotsTypes = onlyCompare ? [getOneOfDotsType(onlyCompare)] : Object.values(TraitDotsType)
    const groups = getGroupClassNames(Object.values(SVGComponents), traitDotsTypes)
    svg.selectAll(groups.join(', ')).remove()
  }

  /**
   * Draws Frame, Empty State, Nodes, Links ...
   */
  draw(): void {
    this.drawHandler(({ svg, ...args }: DrawHandlerCallBackArgs, drawArgs: DrawHandlerDrawArgs) => {
      svg.selectAll('*').remove()
      this.isInEmptyState = false

      this.frameRef.drawFrame(svg, pick(args, 'xScale', 'yScale', 'width', 'height', 'fontSize'), this.loading)

      if (!this.#nodes) {
        this.drawEmptyState(svg, drawArgs.drawEmptyStateArgs)
        return
      }

      if (this.#nodes && this.#connections) {
        if (this.#cLine) {
          this.cLinesRef.drawCLine(svg, { ...drawArgs.drawCLinesArgs, line: this.#cLine }, this.loading)
        }
        this.connectionsRef.drawConnections(
          svg,
          { ...drawArgs.drawConnectionsArgs, connections: this.#connections, nodes: this.#nodes },
          this.loading
        )
        this.nodesRef.drawNodes(svg, { ...drawArgs.drawNodesArgs, nodes: this.#nodes }, this.loading)
      }

      if (this.#compareNodes && this.#connections) {
        this.connectionsRef.drawConnections(
          svg,
          { ...drawArgs.drawConnectionsArgs, connections: this.#connections, nodes: this.#compareNodes, compare: true },
          this.loading
        )
        this.nodesRef.drawNodes(svg, { ...drawArgs.drawNodesArgs, nodes: this.#compareNodes, compare: true }, this.loading)

        if (this.#compareCLine) {
          this.cLinesRef.drawCLine(svg, { ...drawArgs.drawCLinesArgs, line: this.#compareCLine, compare: true }, this.loading)
        }
      }
    })
  }

  /**
   * Updates the draw for Links, Nodes, Empty State, Frame
   */
  drawUpdate() {
    this.drawHandler(({ svg }: DrawHandlerCallBackArgs, drawArgs: DrawHandlerDrawArgs) => {
      this.frameRef.updateFrame(svg, this.loading)

      if (!this.#nodes) {
        this.cleanNodesAndLinks(svg)
        this.drawEmptyState(svg, drawArgs.drawEmptyStateArgs)
        return
      }

      if (this.#nodes && this.#connections) {
        if (this.#cLine) {
          this.cLinesRef.updateCLine(svg, { ...drawArgs.drawCLinesArgs, line: this.#cLine }, this.loading)
        }
        this.connectionsRef.updateConnections(
          svg,
          { ...drawArgs.drawConnectionsArgs, connections: this.#connections, nodes: this.#nodes },
          this.loading
        )
        this.nodesRef.updateNodes(svg, { ...drawArgs.drawNodesArgs, nodes: this.#nodes }, this.loading)
      } else {
        this.cleanNodesAndLinks(svg)
      }

      if (this.#compareNodes && this.#connections) {
        this.connectionsRef.updateConnections(
          svg,
          { ...drawArgs.drawConnectionsArgs, connections: this.#connections, nodes: this.#compareNodes, compare: true },
          this.loading
        )
        this.nodesRef.updateNodes(svg, { ...drawArgs.drawNodesArgs, nodes: this.#compareNodes, compare: true }, this.loading)

        if (this.#compareCLine) {
          this.cLinesRef.updateCLine(svg, { ...drawArgs.drawCLinesArgs, line: this.#compareCLine, compare: true }, this.loading)
        }
      } else {
        this.cleanNodesAndLinks(svg, true)
      }
    })
  }

  drawHandler(cb: (args: DrawHandlerCallBackArgs, drawArgs: DrawHandlerDrawArgs) => unknown) {
    if (!this.svgRef?.nativeElement) return

    const svg = d3.select(this.svgRef.nativeElement)

    const dimensions = calculateDimensions(this.svgRef, this.coreConfig.margin)
    const { innerHeight, innerWidth } = dimensions
    const scales = getScales({ innerHeight, innerWidth }, this.coreConfig)
    const fontSize = getFontSize({ innerWidth })

    const cbArgs = { ...dimensions, ...scales, fontSize, svg }

    const drawConnectionsArgs = pick(cbArgs, 'xScale', 'yScale')
    const drawNodesArgs = { ...drawConnectionsArgs, ...pick(cbArgs, 'innerHeight') }
    const drawCLinesArgs = { ...drawNodesArgs }
    const drawEmptyStateArgs = { ...drawConnectionsArgs, ...pick(cbArgs, 'fontSize') }

    const drawArgs = { drawConnectionsArgs, drawNodesArgs, drawCLinesArgs, drawEmptyStateArgs }

    cb(cbArgs, drawArgs)
  }
}
